import type { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestType } from '@econtractor/types';
import SvgTrash from 'components/svg/SvgTrash';
import { SUPPLIER_REQUEST_STATUS } from './model/constants';
import useRequestStatusActions from './model/useRequestStatusActions';

interface RequestActionsProps {
    row: SupplierRequestType;
    canOps: boolean;
    onOpen: (requestId: number) => void;
    onClone: (row: SupplierRequestType) => void;
    onChanged: () => void;
}

const RequestActions = ({ row, canOps, onOpen, onClone, onChanged }: RequestActionsProps) => {
    const { t } = useTranslation(['Warehouses']);
    const { confirmMarkDone, confirmMarkDeleted } = useRequestStatusActions({ onChanged });

    const status = Number(row.status);
    const requestId = Number(row.ID);
    const vendorName = String(row.vendorName || '').trim() || `#${row.vendorId}`;
    const canClone = canOps;
    const canMarkDone = canOps && status === SUPPLIER_REQUEST_STATUS.ACTIVE;
    const canDelete =
        canOps &&
        (status === SUPPLIER_REQUEST_STATUS.ACTIVE || status === SUPPLIER_REQUEST_STATUS.DONE);

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <i className="bi bi-eye"></i>,
                t('supplierRequests.actions.open'),
                t('supplierRequests.actions.openHint'),
                () => onOpen(requestId),
            )}
            {canClone
                ? renderMenuItem(
                      <i className="bi bi-copy"></i>,
                      t('supplierRequests.actions.clone'),
                      t('supplierRequests.actions.cloneHint'),
                      () => onClone(row),
                  )
                : null}
            {canMarkDone || canDelete ? <Dropdown.Divider className="my-2" /> : null}
            {canMarkDone
                ? renderMenuItem(
                      <i className="bi bi-check2-circle"></i>,
                      t('supplierRequests.actions.done'),
                      t('supplierRequests.actions.doneHint'),
                      () => confirmMarkDone(requestId, vendorName),
                  )
                : null}
            {canMarkDone && canDelete ? <Dropdown.Divider className="my-2" /> : null}
            {canDelete
                ? renderMenuItem(
                      <SvgTrash />,
                      t('supplierRequests.actions.delete'),
                      t('supplierRequests.actions.deleteHint'),
                      () => confirmMarkDeleted(requestId, vendorName),
                      { danger: true },
                  )
                : null}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-supplier-request-${requestId}`}
            >
                <i className="bi bi-three-dots"></i>
            </Dropdown.Toggle>
            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default RequestActions;
